"use client";

import type { ReactNode } from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { AccountMenu } from "./account-menu";
import { NavShell } from "./nav-shell";
import { NavLink, SignedOutLinks } from "./site-nav";
import { UpgradeButton } from "./upgrade-button";
import { FeedbackButton } from "./feedback-button";

function SignedInActions({ children }: { children: ReactNode }) {
  return (
    <div className="pz-navactions">
      <FeedbackButton />
      {children}
    </div>
  );
}

/**
 * The in-app top-bar nav (account layout + editor), the `SiteNav` analog for
 * signed-in surfaces. Same config-gating as `SiteNav`:
 *   - Keyless (no `NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY`): prop-driven, no Clerk
 *     client hooks.
 *   - Clerk configured: reads real signed-in state from `useAuth()` (falling
 *     back to the server-provided `signedIn` hint before Clerk loads).
 * `isPaid` is always a server-computed prop (entitlement, not Clerk identity),
 * so it flows through both paths unchanged.
 *
 * The env is read at render (not module scope) so tests can toggle the path.
 */

/** Signed-in branch, shared by both config paths: My maps / Help / Upgrade (gated). */
function SignedInLinks({ isPaid }: { isPaid: boolean }) {
  return (
    <>
      <NavLink href="/my-maps/" label="My maps" />
      <NavLink href="/help/" label="Help" />
      {!isPaid ? <UpgradeButton /> : null}
    </>
  );
}

/** Clerk-configured path: real client auth state + the account menu. */
function AuthAppNav({
  initialSignedIn,
  isPaid,
  isAdmin,
}: {
  initialSignedIn: boolean;
  isPaid: boolean;
  isAdmin: boolean;
}) {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  // Before Clerk hydrates (and during SSR) fall back to the server hint so the
  // bar doesn't flash the wrong state on surfaces that already know it.
  const signedIn = isLoaded ? Boolean(isSignedIn) : initialSignedIn;

  if (!signedIn) {
    return (
      <NavShell className="pz-appnav">
        <SignedOutLinks />
      </NavShell>
    );
  }

  return (
    <NavShell
      className="pz-appnav"
      trailing={
        <SignedInActions>
          <AccountMenu
            name={user?.fullName ?? null}
            email={user?.primaryEmailAddress?.emailAddress ?? null}
            isAdmin={isAdmin}
          />
        </SignedInActions>
      }
    >
      <SignedInLinks isPaid={isPaid} />
    </NavShell>
  );
}

/** Keyless path: prop-driven, no Clerk client hooks. */
function StaticAppNav({ signedIn, isPaid, isAdmin }: { signedIn: boolean; isPaid: boolean; isAdmin: boolean }) {
  return signedIn ? (
    <NavShell
      className="pz-appnav"
      trailing={
        <SignedInActions>
          <AccountMenu name={null} email={null} isAdmin={isAdmin} />
        </SignedInActions>
      }
    >
      <SignedInLinks isPaid={isPaid} />
    </NavShell>
  ) : (
    <NavShell className="pz-appnav">
      <SignedOutLinks />
    </NavShell>
  );
}

export function AppNav({ signedIn, isPaid, isAdmin = false }: { signedIn: boolean; isPaid: boolean; isAdmin?: boolean }) {
  const clerkEnabled = Boolean(process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY);
  return clerkEnabled ? (
    <AuthAppNav initialSignedIn={signedIn} isPaid={isPaid} isAdmin={isAdmin} />
  ) : (
    <StaticAppNav signedIn={signedIn} isPaid={isPaid} isAdmin={isAdmin} />
  );
}
